import { DateTime } from "luxon";
import type { HoursSummaryEmployeeDayShift, NurseInShift } from "../../../../utils/models";
import { isAgencyNurse } from "../../../../utils/utilFunctions";

const TIME_FORMAT = "h:mma";
const PUNCHED_TIME_FORMAT = "HH:mm";

export type CostlyRunRow = {
	/** The run's first shift: its id keys the row, and it is what a double-click searches for. */
	shift: NurseInShift;
	isAgency: boolean;
	/** Any shift in the run has punched hours. */
	isActual: boolean;
	/** Summed hours ("16.0h"), or "" when a shift in the run has none — a partial sum would understate it. */
	hoursDisplay: string;
	start: string;
	end: string;
};

/** Actual shifts surface the punched start/end (BE returns them as "HH:mm"); others use the scheduled times. */
function displayTime(
	shift: NurseInShift,
	edge: "start" | "end",
	actualShiftIds: Set<string> | undefined,
	shiftHoursById: Map<string, HoursSummaryEmployeeDayShift> | undefined,
): string {
	const shiftHours = shiftHoursById?.get(shift.id);
	if (actualShiftIds?.has(shift.id) && shiftHours) {
		return DateTime.fromFormat(edge === "start" ? shiftHours.shiftStart : shiftHours.shiftEnd, PUNCHED_TIME_FORMAT).toFormat(TIME_FORMAT);
	}
	return (edge === "start" ? shift.startTime : shift.endTime).toFormat(TIME_FORMAT);
}

/** One High-Cost Shifts row for a run of a nurse's consecutive shifts: first start to last end, hours summed. */
export function describeCostlyRun(
	run: NurseInShift[],
	shiftHoursById: Map<string, HoursSummaryEmployeeDayShift> | undefined,
	actualShiftIds: Set<string> | undefined,
): CostlyRunRow {
	const shift = run[0];
	const lastShift = run.reduce((latest, s) => (s.endTime > latest.endTime ? s : latest));
	const isAgency = isAgencyNurse(shift.type);
	const runHours = run.map((s) => shiftHoursById?.get(s.id)).filter((h): h is HoursSummaryEmployeeDayShift => h !== undefined);
	// Punched hours are looked up per nurse and date, so both shifts of a same-day double can point at
	// the same punched entry; summing distinct entries keeps that one punch from counting twice.
	const totalHours = [...new Set(runHours)].reduce((sum, h) => sum + (isAgency ? h.agencyHours : h.totalHours), 0);
	return {
		shift,
		isAgency,
		isActual: run.some((s) => actualShiftIds?.has(s.id)),
		hoursDisplay: runHours.length === run.length ? `${totalHours.toFixed(1)}h` : "",
		start: displayTime(shift, "start", actualShiftIds, shiftHoursById),
		end: displayTime(lastShift, "end", actualShiftIds, shiftHoursById),
	};
}
